import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import type { ProactivenessSection } from '@/features/im-channel-proactivity-experiments/im-channel-proactivity-experiments-types';
import { useCreateProactivenessRollout } from '@/features/im-channel-proactivity-experiments/useImChannelProactivityExperiments';

import type { FormEvent } from 'react';

type RolloutEditorDialogProps = {
  section: ProactivenessSection;
  onOpenChange: (open: boolean) => void;
};

export const RolloutEditorDialog = ({
  section,
  onOpenChange,
}: RolloutEditorDialogProps) => {
  const createRollout = useCreateProactivenessRollout();

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const posthogUrl = String(form.get('posthog_url')).trim();
    createRollout.mutate(
      {
        slug: String(form.get('slug')),
        section: String(form.get('section')),
        flag_key: String(form.get('flag_key')),
        posthog_url: posthogUrl || undefined,
      },
      { onSuccess: () => onOpenChange(false) }
    );
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit} className="space-y-5">
          <DialogHeader>
            <DialogTitle>New rollout</DialogTitle>
            <DialogDescription>
              Link a PostHog flag to a rollout. Routines and broadcasts can
              share it.
            </DialogDescription>
          </DialogHeader>
          <label className="block space-y-1.5" htmlFor="rollout-section">
            <span className="text-sm font-medium">Section</span>
            <Select
              id="rollout-section"
              name="section"
              defaultValue={section}
              required
            >
              <option value="production">Production</option>
              <option value="experimental">Experiments</option>
            </Select>
          </label>
          <label className="block space-y-1.5" htmlFor="rollout-slug">
            <span className="text-sm font-medium">Stable ID</span>
            <Input
              id="rollout-slug"
              name="slug"
              pattern="[a-z0-9]+(?:[-_][a-z0-9]+)*"
              placeholder="inbox_updates"
              required
            />
          </label>
          <label className="block space-y-1.5" htmlFor="rollout-flag-key">
            <span className="text-sm font-medium">PostHog flag key</span>
            <Input
              id="rollout-flag-key"
              name="flag_key"
              placeholder="proactive-inbox-production"
              required
            />
          </label>
          <label className="block space-y-1.5" htmlFor="rollout-posthog-url">
            <span className="text-sm font-medium">PostHog link (optional)</span>
            <Input
              id="rollout-posthog-url"
              name="posthog_url"
              type="url"
              pattern="[hH][tT][tT][pP][sS]?://.+"
              title="Use an http:// or https:// link."
              placeholder="https://us.posthog.com/project/.../feature_flags/..."
            />
          </label>
          {createRollout.error && (
            <p role="alert" className="text-sm text-destructive">
              {createRollout.error.message}
            </p>
          )}
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={createRollout.isPending}>
              {createRollout.isPending ? 'Creating...' : 'Create rollout'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
